import { useEffect, useId, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { LiveKitRoom, VideoConference } from '@livekit/components-react'
import '@livekit/components-styles'
import {
  VideoCameraIcon,
  PlusIcon,
  XMarkIcon,
  PlayIcon,
  StopIcon,
  ArrowLeftIcon,
  UsersIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline'
import { liveRoomAPI } from '../services/api'
import { useLiveRoomChat } from '../hooks/useLiveRoomChat'
import type { LiveSession, LiveJoinToken, LiveChatMessage } from '../types'
import LoadingScreen from '../components/LoadingScreen'
import Tooltip from '../components/Tooltip'
import { LIVE_ROOM_GLOSSARY } from '../config/glossary'

const STATUS_CONFIG: Record<LiveSession['status'], { label: string; classes: string }> = {
  scheduled: { label: 'Scheduled', classes: 'bg-gray-100 text-gray-600' },
  live: { label: 'Live', classes: 'bg-danger-50 text-danger-600' },
  ended: { label: 'Ended', classes: 'bg-gray-100 text-gray-500' },
  cancelled: { label: 'Cancelled', classes: 'bg-gray-100 text-gray-500' },
}

interface NewSessionModalProps {
  isOpen: boolean
  onClose: () => void
  onCreated: (session: LiveSession) => void
}

const NewSessionModal = ({ isOpen, onClose, onCreated }: NewSessionModalProps) => {
  const titleId = useId()
  const [title, setTitle] = useState('')
  const [scheduledStart, setScheduledStart] = useState('')
  const [maxParticipants, setMaxParticipants] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) onClose()
    }
    if (isOpen) document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isOpen, isSubmitting, onClose])

  const reset = () => {
    setTitle('')
    setScheduledStart('')
    setMaxParticipants('')
  }

  const handleSubmit = async () => {
    if (!title.trim()) { toast.error('Session title is required'); return }

    setIsSubmitting(true)
    try {
      const session = await liveRoomAPI.create({
        title: title.trim(),
        ...(scheduledStart ? { scheduled_start: new Date(scheduledStart).toISOString() } : {}),
        ...(maxParticipants ? { max_participants: parseInt(maxParticipants, 10) } : {}),
      })
      toast.success('Live session created')
      onCreated(session)
      reset()
      onClose()
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to create session')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 id={titleId} className="text-xl font-bold text-gray-900">New Live Session</h2>
          <button onClick={onClose} disabled={isSubmitting} className="text-gray-500 hover:text-gray-700">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input
              type="text"
              placeholder="e.g. Weekly market outlook"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled start</label>
            <input
              type="datetime-local"
              value={scheduledStart}
              onChange={(e) => setScheduledStart(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max participants</label>
            <input
              type="number"
              min="1"
              step="1"
              placeholder="No limit"
              value={maxParticipants}
              onChange={(e) => setMaxParticipants(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="flex gap-3 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 font-medium disabled:opacity-50"
            >
              {isSubmitting ? 'Creating...' : 'Create Session'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface SessionCardProps {
  session: LiveSession
  onUpdated: (session: LiveSession) => void
  onJoin: (sessionId: number) => void
}

const SessionCard = ({ session, onUpdated, onJoin }: SessionCardProps) => {
  const [isBusy, setIsBusy] = useState(false)
  const statusCfg = STATUS_CONFIG[session.status]

  const handleStart = async () => {
    setIsBusy(true)
    try {
      const updated = await liveRoomAPI.start(session.id)
      onUpdated(updated)
      toast.success('Session started')
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to start session')
    } finally {
      setIsBusy(false)
    }
  }

  const handleEnd = async () => {
    setIsBusy(true)
    try {
      const updated = await liveRoomAPI.end(session.id)
      onUpdated(updated)
      toast.success('Session ended')
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to end session')
    } finally {
      setIsBusy(false)
    }
  }

  return (
    <div className="bg-white rounded-lg shadow p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-base font-bold text-gray-900">{session.title}</span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusCfg.classes}`}>
              {statusCfg.label}
            </span>
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
            {session.scheduled_start && (
              <span>
                <span className="text-gray-400">Scheduled:</span>{' '}
                {new Date(session.scheduled_start).toLocaleString()}
              </span>
            )}
            <span className="flex items-center gap-1">
              <UsersIcon className="h-4 w-4 text-gray-400" />
              {session.participant_count}
              {session.max_participants ? ` / ${session.max_participants}` : ''}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {session.status === 'scheduled' && (
            <button
              onClick={handleStart}
              disabled={isBusy}
              title="Start session"
              className="p-2 text-primary-600 hover:bg-primary-50 rounded-md transition-colors disabled:opacity-50"
            >
              <PlayIcon className="h-4 w-4" />
            </button>
          )}
          {session.status === 'live' && (
            <button
              onClick={handleEnd}
              disabled={isBusy}
              title="End session"
              className="p-2 text-danger-500 hover:bg-danger-50 rounded-md transition-colors disabled:opacity-50"
            >
              <StopIcon className="h-4 w-4" />
            </button>
          )}
          {(session.status === 'live' || session.status === 'scheduled') && (
            <button
              onClick={() => onJoin(session.id)}
              className="px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors font-medium text-sm"
            >
              Join
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

interface ChatPanelProps {
  sessionId: number
}

const ChatPanel = ({ sessionId }: ChatPanelProps) => {
  const { messages, sendMessage } = useLiveRoomChat(sessionId)
  const [history, setHistory] = useState<LiveChatMessage[]>([])
  const [draft, setDraft] = useState('')
  const [isLoadingHistory, setIsLoadingHistory] = useState(true)
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    liveRoomAPI.getChatHistory(sessionId)
      .then(setHistory)
      .catch(() => toast.error('Failed to load chat history'))
      .finally(() => setIsLoadingHistory(false))
  }, [sessionId])

  const allMessages = [...history, ...messages]

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [allMessages.length])

  const handleSend = () => {
    if (!draft.trim()) return
    sendMessage(draft.trim())
    setDraft('')
  }

  return (
    <div className="flex flex-col h-full bg-white rounded-lg shadow">
      <div className="border-b border-gray-200 px-4 py-3">
        <h3 className="font-semibold text-gray-900">Chat</h3>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 min-h-[200px] max-h-[400px]">
        {isLoadingHistory ? (
          <p className="text-sm text-gray-400">Loading chat...</p>
        ) : allMessages.length === 0 ? (
          <p className="text-sm text-gray-400">No messages yet. Say hello!</p>
        ) : (
          allMessages.map((m, i) => (
            <div key={m.id ?? i} className="text-sm">
              <span className="font-medium text-gray-900">{m.username}:</span>{' '}
              <span className="text-gray-700">{m.message}</span>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>
      <div className="border-t border-gray-200 p-3 flex items-center gap-2">
        <input
          type="text"
          placeholder="Send a message..."
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSend() }}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <button
          onClick={handleSend}
          title="Send"
          className="p-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
        >
          <PaperAirplaneIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

interface LiveRoomStageProps {
  sessionId: number
  onLeave: () => void
}

const LiveRoomStage = ({ sessionId, onLeave }: LiveRoomStageProps) => {
  const [joinToken, setJoinToken] = useState<LiveJoinToken | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    liveRoomAPI.getJoinToken(sessionId)
      .then(setJoinToken)
      .catch((err: any) => {
        toast.error(err.response?.data?.detail || 'Failed to join session')
        onLeave()
      })
      .finally(() => setIsLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId])

  if (isLoading) return <LoadingScreen />
  if (!joinToken) return null

  return (
    <div className="space-y-4">
      <button
        onClick={onLeave}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Back to sessions
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-black rounded-lg overflow-hidden" style={{ height: 480 }}>
          <LiveKitRoom
            serverUrl={joinToken.ws_url || undefined}
            token={joinToken.token}
            connect
            video
            audio
            onDisconnected={onLeave}
            onError={() => toast.error('Connection to the live room failed')}
            data-lk-theme="default"
            style={{ height: '100%' }}
          >
            <VideoConference />
          </LiveKitRoom>
        </div>
        <div className="lg:col-span-1" style={{ height: 480 }}>
          <ChatPanel sessionId={sessionId} />
        </div>
      </div>
    </div>
  )
}

const LiveRoomDashboard = () => {
  const navigate = useNavigate()
  const [sessions, setSessions] = useState<LiveSession[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    liveRoomAPI.listOwn()
      .then((data) => setSessions(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Failed to load live sessions'))
      .finally(() => setIsLoading(false))
  }, [])

  const handleCreated = (session: LiveSession) => {
    setSessions((prev) => [session, ...prev])
  }

  const handleUpdated = (updated: LiveSession) => {
    setSessions((prev) => prev.map((s) => (s.id === updated.id ? updated : s)))
  }

  const handleJoin = (sessionId: number) => {
    navigate(`/live-room/${sessionId}`)
  }

  if (isLoading) return <LoadingScreen />

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Live Trading Room</h1>
          <p className="text-gray-500 mt-1">
            Host and join{' '}
            <Tooltip content={LIVE_ROOM_GLOSSARY.session}>real-time video sessions</Tooltip> with your audience
          </p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors font-medium"
        >
          <PlusIcon className="h-5 w-5" />
          New Session
        </button>
      </div>

      {sessions.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <VideoCameraIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg font-medium">No live sessions yet</p>
          <p className="text-gray-400 mt-1 mb-5">Schedule a session to go live with your audience.</p>
          <button
            onClick={() => setIsModalOpen(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 font-medium"
          >
            Create Session
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {sessions.map((session) => (
            <SessionCard key={session.id} session={session} onUpdated={handleUpdated} onJoin={handleJoin} />
          ))}
        </div>
      )}

      <NewSessionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreated={handleCreated}
      />
    </div>
  )
}

const LiveRoom = () => {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()

  if (sessionId) {
    const id = parseInt(sessionId, 10)
    if (Number.isNaN(id)) {
      return (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500 text-lg font-medium">Session not found</p>
          <button
            onClick={() => navigate('/live-room')}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 font-medium"
          >
            Back to sessions
          </button>
        </div>
      )
    }
    return <LiveRoomStage sessionId={id} onLeave={() => navigate('/live-room')} />
  }

  return <LiveRoomDashboard />
}

export default LiveRoom
